import React, {useEffect, useState, useRef, useCallback} from "react";
import {Button, Card, Col, DatePicker, Row, Spin, Table, Typography} from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {getCarHardwareEvents} from "../../../helpers/car";
import {messageType, showMessages} from "../../../helpers/message";
import {getDistanceUnit} from "../../../helpers/booking";
import {dateFormat, dateTimeFormat} from "../../../lib/time";

import "./CarHardwareLog.less";

const {RangePicker} = DatePicker;
const {Text} = Typography;

/*
 * This component is a table that is populated with HardwareEvents.
 * Notably key read, ignition start, and ignition end events.
 */
const CarHardwareLog = ({carID}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);

    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(10);

    const [hardwareLogs, setHardwareLogs] = useState([]);
    const [startedAt, setStartedAt] = useState(null);
    const [endedAt, setEndedAt] = useState(null);

    const fetchHardwareEvents = useCallback( async (page, perPage, startedAt, endedAt) => {
        setLoading(true);
        try {
            const abortSignal = abortSignalRef.current;
            let response = await getCarHardwareEvents(
                carID,
                {page, perPage, startedAt, endedAt},
                abortSignal
            );

            if (abortSignal.aborted === false && response.length !== 0) {
                setHardwareLogs(response.data);
                setTotalRows(response.meta.total);
            }

            setLoading(false);
        } catch (error) {
            showMessages(error.message, messageType.error);
            setLoading(false);
            if(error.status_code === 404) window.location.replace('/404');
        }
    }, [carID]);

    // onMount function.
    useEffect(() => {
        fetchHardwareEvents(1, 10, null, null);
    }, [fetchHardwareEvents]);

    // onUnmount cleanup function.
    useEffect(() => {
        const abortController = abortControllerRef.current;
        return function abort() {
            abortController.abort();
        }
    }, []);

    const onDateFilterChange = (values) => {
        if (values !== null) {
            setStartedAt(values[0]);
            setEndedAt(values[1]);
        } else {
            setStartedAt(null);
            setEndedAt(null);
        }
    }

    const resetDateRange = () => {
        setStartedAt(null);
        setEndedAt(null);
        fetchHardwareEvents(page, perPage, null, null);
    }

    const filterHardwareLogs = () => {
        // Call onPageChange to handle a bug where a user is on page 2, then filters and can end up in limbo.
        // Where page >1 doesn't exist and they can't switch to page 1.
        onPageChange(1, perPage)
    }

    const disabledDate = (current) => {
        // Can not select start dates from more than one year ago.
        return current < dayjs().subtract(1, 'years');
    };

    const onPageChange = (page, perPage) => {
        setPage(page);
        setPerPage(perPage);
        fetchHardwareEvents(page, perPage, startedAt, endedAt);
    };

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        onChange: onPageChange,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        onShowSizeChange: onPageChange,
    };

    const distanceUnit = getDistanceUnit().toLowerCase();

    const columns = [
        {
            title: <Typography.Text strong>Event Date</Typography.Text>,
            dataIndex: 'date',
            key: 'date',
            render: date => {
                return <span
                    className="car-hardware-log-date-line-item long-text-wrap">{dayjs(date).format(dateTimeFormat)}</span>
            },
        },
        {
            title: <Typography.Text strong>Event</Typography.Text>,
            dataIndex: 'event',
            key: 'event',
            render: event => {
                return <span className="car-hardware-log-event-line-item long-text-wrap">{event}</span>
            },
        },
        {
            title: <Typography.Text strong>Distance</Typography.Text>,
            dataIndex: 'distance',
            key: 'distance',
            render: distance => {
                return <span
                    className="car-hardware-log-distance-line-item long-text-wrap">{distance} {distanceUnit}</span>
            },
        },
        {
            title: <Typography.Text strong>Fob ID</Typography.Text>,
            dataIndex: 'fob_id',
            key: 'fob_id',
            render: fob_id => {
                return <span
                    className="car-hardware-log-fob-id-line-item long-text-wrap">{fob_id ? fob_id : 'N/A'}</span>
            },
        },
        {
            title: <Typography.Text strong>Driver</Typography.Text>,
            dataIndex: 'person',
            key: 'person',
            render: person => {
                return <span className="car-hardware-log-driver-line-item long-text-wrap">{person}</span>
            },
        },
    ];

    return (
        <>
            <Card bordered={true}>
                <Row>
                    <Col xs={24} md={16}>
                        <Text>Date filter:</Text>
                        <RangePicker
                            className="date-filter"
                            value={[startedAt, endedAt]}
                            format={dateFormat}
                            presets={{
                                Today: [dayjs(), dayjs()]
                            }}
                            onChange={onDateFilterChange}
                            disabledDate={disabledDate}
                        />
                    </Col>
                    <Col xs={24} md={8} className="date-filter-button-wrapper">
                        <Button className="reset-btn" onClick={resetDateRange}>Reset</Button>
                        <Button type="primary" onClick={filterHardwareLogs}>Filter</Button>
                    </Col>
                </Row>
            </Card>
            <br/>
            <Spin spinning={loading}>
                <Table
                    rowKey="id"
                    dataSource={hardwareLogs}
                    columns={columns}
                    bordered={false}
                    className="responsive-table"
                    title={() => <Typography.Text>Log History</Typography.Text>}
                    pagination={paginationAttr}
                />
            </Spin>
        </>
    )
}

CarHardwareLog.propTypes = {
    carID: PropTypes.number.isRequired
};

export default CarHardwareLog;
